import React, { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { useShallow } from "zustand/react/shallow";

import useTripStore from "@/stores/tripBuilder/useTrip";
import useCurrencyStore from "@/stores/ui/currencyStore";
import useAccommodationStore from "@/stores/tripBuilder/useAccommodation";
import useCustomAccommodationStore from "@/stores/tripBuilder/useCustomAccommodation";
import { accommodationService } from "@/services/accommodationService";
import { TripLodging } from "@/types/accommodation";
import { FormData } from "./types";
import { questions } from "./questions";
import PreviewCard from "./PreviewCard";
import { useHandleNext } from "./navigationUtils";
import {
  NameStep,
  LocationStep,
  PriceStep,
  TimeStep,
  ImageStep,
  AmenityStep,
  NavigationButtons,
} from "./steps";

// Memoized question title component
const QuestionTitle = React.memo(
  ({ question }: { question: string | undefined }) => (
    <h2 className="text-md font-medium text-neutral-800">{question}</h2>
  )
);

// Memoized preview section header
const PreviewHeader = React.memo(() => (
  <div className="flex items-center gap-2 mb-3">
    <p className="text-base rounded-md px-2 py-1 bg-indigo-50 text-indigo-700 font-medium">
      Preview
    </p>
    <span className="text-xs text-neutral-500 2xl:text-sm">
      Review your property details before adding it to your trip
    </span>
  </div>
));

// Individual step wrappers with direct store access
const NameStepWrapper = React.memo(() => {
  const name = useCustomAccommodationStore((state) => state.name);
  const setName = useCustomAccommodationStore((state) => state.setName);
  const currentStep = useCustomAccommodationStore(
    (state) => state.currentStepIndex
  );
  const setCurrentStep = useState<number>(currentStep)[1];
  const { handleNext } = useHandleNext();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleNext(currentStep, setCurrentStep, questions.length);
    }
  };

  return (
    <NameStep value={name} onChange={handleChange} onKeyDown={handleKeyDown} />
  );
});

const LocationStepWrapper = React.memo(() => {
  const location = useCustomAccommodationStore((state) => state.location);
  const name = useCustomAccommodationStore((state) => state.name);
  const setLocation = useCustomAccommodationStore((state) => state.setLocation);
  const currentStep = useCustomAccommodationStore(
    (state) => state.currentStepIndex
  );
  const setCurrentStep = useCustomAccommodationStore(
    (state) => state.setCurrentStepIndex
  );

  const { handleNext } = useHandleNext();

  const handleChange = (newLocation: any) => {
    setLocation(newLocation);
  };

  const onNextStep = useCallback(
    (locationData?: any) => {
      handleNext(currentStep, setCurrentStep, questions.length, locationData);
    },
    [currentStep, handleNext, setCurrentStep]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleNext(currentStep, setCurrentStep, questions.length);
      }
    },
    [currentStep, handleNext, setCurrentStep]
  );

  return (
    <LocationStep
      value={location}
      onChange={handleChange}
      activityName={name}
      handleNext={onNextStep}
      onKeyDown={handleKeyDown}
    />
  );
});

const PriceStepWrapper = React.memo(() => {
  const pricePerNight = useCustomAccommodationStore(
    (state) => state.pricePerNight
  );
  const totalPrice = useCustomAccommodationStore((state) => state.totalPrice);
  const priceType = useCustomAccommodationStore((state) => state.priceType);
  const setPricePerNight = useCustomAccommodationStore(
    (state) => state.setPricePerNight
  );
  const setTotalPrice = useCustomAccommodationStore(
    (state) => state.setTotalPrice
  );
  const setPriceType = useCustomAccommodationStore(
    (state) => state.setPriceType
  );
  const currentStep = useCustomAccommodationStore(
    (state) => state.currentStepIndex
  );
  const setCurrentStep = useState<number>(currentStep)[1];
  const { handleNext } = useHandleNext();

  const handlePriceChange = (name: string, value: string) => {
    if (name === "pricePerNight") {
      setPricePerNight(value);
    } else if (name === "totalPrice") {
      setTotalPrice(value);
    }
  };

  const handlePriceTypeChange = (type: "perNight" | "total") => {
    setPriceType(type);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleNext(currentStep, setCurrentStep, questions.length);
    }
  };

  return (
    <PriceStep
      pricePerNight={pricePerNight}
      totalPrice={totalPrice}
      priceType={priceType}
      onPriceChange={handlePriceChange}
      onPriceTypeChange={handlePriceTypeChange}
      onKeyDown={handleKeyDown}
    />
  );
});

const TimeStepWrapper = React.memo(() => {
  const checkInTime = useCustomAccommodationStore((state) => state.checkInTime);
  const checkOutTime = useCustomAccommodationStore(
    (state) => state.checkOutTime
  );
  const setCheckInTime = useCustomAccommodationStore(
    (state) => state.setCheckInTime
  );
  const setCheckOutTime = useCustomAccommodationStore(
    (state) => state.setCheckOutTime
  );
  const currentStep = useCustomAccommodationStore(
    (state) => state.currentStepIndex
  );
  const setCurrentStep = useState<number>(currentStep)[1];
  const { handleNext } = useHandleNext();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleNext(currentStep, setCurrentStep, questions.length);
    }
  };

  return (
    <TimeStep
      checkInTime={checkInTime}
      checkOutTime={checkOutTime}
      onCheckInTimeChange={setCheckInTime}
      onCheckOutTimeChange={setCheckOutTime}
      onKeyDown={handleKeyDown}
    />
  );
});

const AmenityStepWrapper = React.memo(() => {
  const amenities = useCustomAccommodationStore((state) => state.amenities);
  const toggleAmenity = useCustomAccommodationStore(
    (state) => state.toggleAmenity
  );
  const currentStep = useCustomAccommodationStore(
    (state) => state.currentStepIndex
  );
  const setCurrentStep = useState<number>(currentStep)[1];
  const { handleNext } = useHandleNext();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleNext(currentStep, setCurrentStep, questions.length);
    }
  };

  return (
    <AmenityStep
      selectedAmenities={amenities}
      onAmenityToggle={toggleAmenity}
      onKeyDown={handleKeyDown}
    />
  );
});

const ImageStepWrapper = React.memo(() => {
  const { toast } = useToast();
  const images = useCustomAccommodationStore((state) => state.images);
  const uploadingImages = useCustomAccommodationStore(
    (state) => state.uploadingImages
  );
  const setUploadingImages = useCustomAccommodationStore(
    (state) => state.setUploadingImages
  );
  const addImage = useCustomAccommodationStore((state) => state.addImage);
  const removeImage = useCustomAccommodationStore((state) => state.removeImage);
  const currentStep = useCustomAccommodationStore(
    (state) => state.currentStepIndex
  );
  const setCurrentStep = useState<number>(currentStep)[1];
  const { handleNext } = useHandleNext();

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const remainingSlots = 4 - images.length;
    if (remainingSlots <= 0) {
      toast({
        title: "Maximum images reached",
        description: "You can only upload up to 4 images",
        variant: "destructive",
      });
      return;
    }

    // Validate each file before uploading
    const validFiles: File[] = [];
    const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB in bytes

    Array.from(files).forEach((file) => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description: `${file.name} is not a valid image type. Please use JPG, PNG, or WebP.`,
          variant: "destructive",
        });
        return;
      }

      if (file.size > MAX_SIZE) {
        toast({
          title: "File too large",
          description: `${file.name} exceeds 10MB limit.`,
          variant: "destructive",
        });
        return;
      }

      validFiles.push(file);
    });

    if (validFiles.length === 0) return;

    // Only take up to remaining slots
    const filesToUpload = validFiles.slice(0, remainingSlots);
    setUploadingImages(true);

    try {
      // First, upload the images and get URLs
      const uploadResponse =
        await accommodationService.uploadCustomAccommodationImages(
          filesToUpload
        );
      const imageUrls = uploadResponse.data.urls;

      // Then update with files and URLs
      filesToUpload.forEach((file: File, index: number) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          addImage({
            file,
            preview: reader.result as string,
            url: imageUrls[index],
          });
        };
        reader.readAsDataURL(file);
      });
    } catch (error: unknown) {
      console.error("Error uploading images:", error);
      toast({
        title: "Upload failed",
        description:
          "There was an error uploading your images. Please try again.",
        variant: "destructive",
      });
    } finally {
      setUploadingImages(false);
    }
  };

  const onNextStep = useCallback(() => {
    handleNext(currentStep, setCurrentStep, questions.length);
  }, [currentStep, handleNext, setCurrentStep]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleNext(currentStep, setCurrentStep, questions.length);
      }
    },
    [currentStep, handleNext, setCurrentStep]
  );

  return (
    <ImageStep
      images={images}
      uploadingImages={uploadingImages}
      handleImageUpload={handleImageUpload}
      removeImage={removeImage}
      onNext={onNextStep}
      onKeyDown={handleKeyDown}
    />
  );
});

// Memoized Preview component with optimized state selection
const Preview = React.memo(() => {
  // Use the useShallow hook to create a selector with shallow equality
  const formData = useCustomAccommodationStore(
    useShallow((state) => ({
      name: state.name,
      location: state.location,
      pricePerNight: state.pricePerNight,
      totalPrice: state.totalPrice,
      priceType: state.priceType,
      numNights: state.numNights,
      numGuests: state.numGuests,
      checkInTime: state.checkInTime,
      checkOutTime: state.checkOutTime,
      amenities: state.amenities,
      images: state.images,
    }))
  ) as FormData;

  const selectedCurrency = useCurrencyStore((state) => state.selectedCurrency);
  const { selectedLocation } = useTripStore();

  return (
    <div className="flex flex-col h-[35%]">
      <PreviewHeader />
      <div className="flex-1 border-neutral-200 border rounded-lg p-3 bg-white overflow-hidden min-h-0">
        <PreviewCard
          formData={formData}
          selectedCurrency={selectedCurrency}
          timeZone={selectedLocation?.date.time_zone || "UTC"}
        />
      </div>
    </div>
  );
});

const CustomAccommodationForm = () => {
  const { toast } = useToast();
  // const [currentStep, setCurrentStep] = useState(0);
  const { handleNext } = useHandleNext();
  const { selectedLocation } = useTripStore();
  const { selectedCurrency } = useCurrencyStore();
  const { addLodging } = useAccommodationStore();

  // These properties come from the store
  const currentStep = useCustomAccommodationStore(
    (state) => state.currentStepIndex
  );
  const setCurrentStep = useCustomAccommodationStore(
    (state) => state.setCurrentStepIndex
  );
  const reset = useCustomAccommodationStore((state) => state.reset);
  const name = useCustomAccommodationStore((state) => state.name);
  const location = useCustomAccommodationStore((state) => state.location);
  const pricePerNight = useCustomAccommodationStore(
    (state) => state.pricePerNight
  );
  const totalPrice = useCustomAccommodationStore((state) => state.totalPrice);
  const priceType = useCustomAccommodationStore((state) => state.priceType);
  const checkInTime = useCustomAccommodationStore((state) => state.checkInTime);
  const checkOutTime = useCustomAccommodationStore(
    (state) => state.checkOutTime
  );
  const images = useCustomAccommodationStore((state) => state.images);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep, setCurrentStep]);

  // Render current step based on step index
  const renderCurrentStep = useCallback(() => {
    const currentQuestion = questions[currentStep];
    if (!currentQuestion) return null;

    switch (currentQuestion.type) {
      case "input":
        return <NameStepWrapper />;

      case "location":
        return <LocationStepWrapper />;

      case "price":
        return <PriceStepWrapper />;

      case "time":
        return <TimeStepWrapper />;

      case "amenities":
        return <AmenityStepWrapper />;

      case "images":
        return <ImageStepWrapper />;

      default:
        return null;
    }
  }, [currentStep]);

  const handleNextStep = useCallback(async () => {
    if (currentStep < questions.length - 1) {
      handleNext(currentStep, setCurrentStep, questions.length);
    } else {
      if (selectedLocation?.date.num_days) {
        try {
          // Create the TripLodging data
          const lodgingData: TripLodging = {
            id: Date.now(),
            name: name,
            type: "custom",
            description: `You're staying at ${name} in ${selectedLocation?.name}`,
            policies: {
              checkIn: checkInTime,
              checkOut: checkOutTime,
            },
            duration: selectedLocation?.date.num_days,
            pricing: {
              amount:
                priceType === "perNight"
                  ? parseFloat(pricePerNight) || 0
                  : (parseFloat(totalPrice) || 0) /
                    selectedLocation?.date.num_days,
              total:
                priceType === "perNight"
                  ? (parseFloat(pricePerNight) || 0) *
                    selectedLocation?.date.num_days
                  : parseFloat(totalPrice) || 0,
              currency: selectedCurrency.code,
            },
            coordinates: {
              latitude: location.coordinates.latitude,
              longitude: location.coordinates.longitude,
            },
            mainImageURL: images[0]?.url || "",
            address: location.value,
            bookingStatus: "pending",
            moreInfo: {
              propertyType: "Custom accommodation",
            },
          };

          // Add the lodging to the store
          addLodging(lodgingData);

          toast({
            title: "Success",
            description: "Accommodation added to your trip",
          });

          // Reset form
          reset();
          setCurrentStep(0);
        } catch (error) {
          console.error("Error adding accommodation:", error);
          toast({
            title: "Error",
            description: "Failed to add accommodation. Please try again.",
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Error",
          description: "Failed to add accommodation. Please try again.",
          variant: "destructive",
        });
      }
    }
  }, [
    currentStep,
    selectedLocation,
    name,
    checkInTime,
    checkOutTime,
    priceType,
    pricePerNight,
    totalPrice,
    location,
    images,
    addLodging,
    selectedCurrency.code,
    handleNext,
    toast,
    reset,
    setCurrentStep,
  ]);

  return (
    <div className="h-[calc(100vh-7rem)] w-full rounded-md px-2 pb-2">
      <div className="h-full flex flex-col">
        {/* Main content area */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {/* Scrollable question area */}
          <div className="h-[calc(100%-3rem)] overflow-y-auto">
            <div className="min-h-full flex items-start px-1">
              <div className="w-full items-center justify-center">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="mt-2"
                  >
                    <div className="space-y-5">
                      <div className="space-y-1">
                        <QuestionTitle
                          question={questions[currentStep]?.question}
                        />
                      </div>

                      <div className="">{renderCurrentStep()}</div>

                      <NavigationButtons
                        currentStep={currentStep}
                        handleBack={handleBack}
                        handleNext={handleNextStep}
                        isLastStep={currentStep === questions.length - 1}
                      />
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

        {/* Preview section */}
        <Preview />
      </div>
    </div>
  );
};

export default CustomAccommodationForm;
